/**
 * Der Rahmen aller Ansichten vor der Anmeldung — Anmelden, Registrieren,
 * Passwort vergessen, neues Passwort, Adresse bestätigen.
 *
 * Aufbau aus `templates/auth-web/AuthWeb.dc.html` des Design-System-Projekts:
 * links eine grüne Markenfläche, rechts das Formular, dazwischen keine Naht.
 * Unter etwa 900 px rückt die Markenfläche über das Formular — das erledigt
 * `flex-wrap` und keine Medienabfrage: Maßgeblich ist der Platz, den die Karte
 * hat, und nicht die Breite des Fensters.
 *
 * **Ein Rahmen für alle fünf Ansichten und nicht zwei.** Vorher hatten
 * Anmeldung und Wiederherstellung je einen eigenen, gleich aussehenden
 * Nachbau; wer den einen änderte, ließ den anderen stehen. Wer aus einer E-Mail
 * hierherkommt, soll sehen, dass er bei derselben Anwendung gelandet ist.
 */
import { Card, Wordmark } from "@hausmeter/ui";
import { Fragment, type CSSProperties, type ReactNode } from "react";
import { navigate, routePath, type LegalDocument, type Route } from "../lib/router.js";

export interface AuthLayoutProps {
  /** Die Zeile auf der Markenfläche. Sie sagt, wozu diese Ansicht führt. */
  readonly headline: string;
  readonly lead: string;
  readonly children: ReactNode;
}

export function AuthLayout({ headline, lead, children }: AuthLayoutProps) {
  return (
    <main
      style={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(16px, 3vw, 48px)",
      }}
    >
      {/*
        `Card` bringt Grund, Haarlinie, Radius und Schatten aus dem
        Design-System mit und schneidet die grüne Fläche an der Ecke ab. Ihre
        innere Fläche ist ein eigenes Element, deshalb sitzt die Flex-Zeile eine
        Ebene tiefer und nicht an der Karte selbst.
      */}
      <Card padding="none" style={{ width: "100%", maxWidth: 1080 }}>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          <aside
            style={{
              flex: "1 1 380px",
              /* Nicht `--hm-green-600`: Das ist im dunklen Thema ein Tint, und
                 die weiße Schrift darauf war nicht mehr zu lesen (HM-107). */
              background: "var(--surface-brand-strong)",
              padding: "clamp(28px, 4vw, 48px)",
              minHeight: "clamp(220px, 28vw, 620px)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: "var(--sp-8)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Wordmark
                tone="invert"
                size={20}
                logoSize={36}
                logo={
                  <img
                    src="/logo.png"
                    alt=""
                    width={36}
                    height={36}
                    /* Die Bildmarke ist für hellen Grund gezeichnet; auf dem
                       Grün braucht sie ihre eigene weiße Fläche. */
                    style={{ background: "var(--surface-card)", objectFit: "contain" }}
                  />
                }
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-6)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
                <h1
                  style={{
                    margin: 0,
                    fontFamily: "var(--font-display)",
                    fontWeight: 800,
                    fontSize: "clamp(26px, 3.2vw, 40px)",
                    lineHeight: 1.15,
                    letterSpacing: "var(--tracking-tight)",
                    color: "var(--text-invert)",
                  }}
                >
                  {headline}
                </h1>
                <p
                  style={{
                    margin: 0,
                    maxWidth: "38ch",
                    fontSize: "clamp(14px, 1.2vw, 16px)",
                    lineHeight: 1.6,
                    /* Kein zweiter Weißton als Hexwert: Die gedämpfte Schrift ist
                     dieselbe Farbe mit weniger Deckung. */
                    color: "color-mix(in srgb, var(--text-invert) 82%, transparent)",
                  }}
                >
                  {lead}
                </p>
              </div>

              <BrandFooter />
            </div>
          </aside>

          <div
            style={{
              flex: "1 1 460px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "clamp(28px, 4vw, 48px)",
            }}
          >
            <div
              style={{
                width: "min(400px, 100%)",
                display: "flex",
                flexDirection: "column",
                gap: "var(--sp-6)",
              }}
            >
              {children}
            </div>
          </div>
        </div>
      </Card>
    </main>
  );
}

/**
 * Die Fußzeile der Markenfläche — Impressum, Datenschutz, Nutzungsbedingungen
 * (HM-061).
 *
 * **Sie hat lange gefehlt, und das war Absicht.** Der Entwurf
 * `templates/auth-web/` führt sie von Anfang an, samt „Status"; umgesetzt war
 * sie nicht, weil es keines der Dokumente als Seite dieser Anwendung gab
 * (OQ-033). Ein `href="#"` an dieser Stelle sähe aus wie das Angebot, vor der
 * Zustimmung zu lesen, und wäre keines.
 *
 * **„Status" fehlt weiter**, und aus demselben Grund: Eine Statusseite gibt es
 * nicht. Zwei von drei Links einzulösen und den dritten ins Leere zeigen zu
 * lassen wäre schlechter als die Fußzeile, die es bis hierher gar nicht gab.
 *
 * Sie steht auf der **Markenfläche** und nicht unter dem Formular. Dort wäre
 * sie der letzte Tabulatorstopp vor dem Absenden — man käme auf dem Weg zum
 * Knopf durch drei Links, die alle die Seite verlassen.
 */
function BrandFooter() {
  return (
    <nav
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "var(--sp-2)",
        fontSize: 12.5,
      }}
    >
      {/*
        **`Fragment` und kein `<span>` um Trenner und Link.** Der Wrapper wäre
        ein `inline-flex` ohne `align-items`, also `stretch`: Ist der Trenner
        aus irgendeinem Grund höher als der Link — eine andere Schriftgröße
        genügt —, wird der Link mitgezogen und sein Text sitzt oben im gedehnten
        Kasten. Genau das ist der Fußzeile der Seitenleiste passiert. Flach
        gelegt sind alle Einträge direkte Kinder derselben Flex-Zeile, und deren
        `alignItems: "center"` gilt für jeden einzeln.
      */}
      {LEGAL_LINKS.map((entry, index) => (
        <Fragment key={entry.document}>
          {index > 0 && (
            <span
              aria-hidden="true"
              /* Dieselbe Größe wie der Link daneben. */
              style={{
                fontSize: 12.5,
                lineHeight: 1,
                color: "color-mix(in srgb, var(--text-invert) 45%, transparent)",
              }}
            >
              ·
            </span>
          )}
          <AuthLink
            to={{ kind: "legal", document: entry.document }}
            style={{
              fontSize: 12.5,
              lineHeight: 1,
              fontWeight: 500,
              /* Kein zweiter Weißton als Hexwert — dieselbe Farbe mit weniger
                 Deckung, wie beim Beisatz darüber. */
              color: "color-mix(in srgb, var(--text-invert) 72%, transparent)",
            }}
          >
            {entry.label}
          </AuthLink>
        </Fragment>
      ))}
    </nav>
  );
}

const LEGAL_LINKS: readonly { readonly document: LegalDocument; readonly label: string }[] = [
  { document: "imprint", label: "Impressum" },
  { document: "privacy", label: "Datenschutz" },
  { document: "terms", label: "Nutzungsbedingungen" },
];

/**
 * Überschrift und Beisatz über dem Formular.
 *
 * **`overflow-wrap: anywhere` am Beisatz ist kein Feinschliff.** Im zweiten
 * Schritt der Registrierung steht dort die eingegebene E-Mail-Adresse, und die
 * enthält kein Leerzeichen: Ohne die Angabe bestimmt sie als längstes
 * unteilbares Wort die Mindestbreite der Formularspalte, und ein Konto mit
 * langer Adresse schöbe die Spalte über den Rand der Karte — derselbe Fehler,
 * an dem die Kästchen des Sicherheitscodes hingen (`code-field.tsx`). Und
 * `anywhere` und nicht `break-word`: Nur die erste Fassung wirkt auf die
 * Größenermittlung, die zweite bricht bloß beim Zeichnen um.
 */
export function AuthHeading({ title, lead }: { readonly title: string; readonly lead: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-1)" }}>
      <h2
        style={{
          margin: 0,
          fontFamily: "var(--font-display)",
          fontWeight: 800,
          fontSize: "clamp(24px, 2.6vw, 30px)",
          letterSpacing: "var(--tracking-tight)",
          color: "var(--text-strong)",
        }}
      >
        {title}
      </h2>
      <p
        style={{
          margin: 0,
          fontSize: 15,
          lineHeight: 1.5,
          color: "var(--text-muted)",
          overflowWrap: "anywhere",
        }}
      >
        {lead}
      </p>
    </div>
  );
}

/**
 * Ein Link auf eine andere Adresse — als echtes `<a href>` und nicht als
 * `div` mit `onClick`. Der gewöhnliche Klick bleibt in der Anwendung; Strg-,
 * Cmd- und Mittelklick gehen an den Browser und öffnen einen neuen Tab.
 */
export function AuthLink({
  to,
  children,
  style,
}: {
  readonly to: Route;
  readonly children: ReactNode;
  readonly style?: CSSProperties;
}) {
  return (
    <a
      href={routePath(to)}
      onClick={(event) => {
        /**
         * **Der Klick endet hier und steigt nicht weiter auf.** Seit HM-061
         * steht ein solcher Link im Text eines `<label>` — dem Haken bei den
         * Nutzungsbedingungen. Ein Label schaltet sein Feld um, sobald der
         * Klick es erreicht; ohne diese Zeile setzte ein Strg-Klick auf
         * „Nutzungsbedingungen" nebenbei den Haken, während der Text in einem
         * neuen Tab aufgeht. Für einen Link, der allein steht, ändert es
         * nichts.
         */
        event.stopPropagation();
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(to);
      }}
      style={{
        fontSize: 13.5,
        fontWeight: 600,
        color: "var(--text-brand)",
        textDecoration: "none",
        ...style,
      }}
    >
      {children}
    </a>
  );
}

/**
 * Der Wechsel zwischen Anmelden und Registrieren. **Kein Link**, obwohl er im
 * Entwurf wie einer aussieht: Die Anmeldung hat keine eigene Adresse (siehe
 * `app.tsx`), und ein `href="#"` wäre ein Versprechen, das die Zurück-Taste
 * einlösen müsste.
 */
export function AuthSwitch({
  onClick,
  children,
  disabled = false,
}: {
  readonly onClick: () => void;
  readonly children: ReactNode;
  /**
   * **Gesperrt, nicht ausgeblendet** — die Ausnahme von Regel 10, und sie hat
   * einen Grund (HM-069). Ausgeblendet wird, was für *diesen Nutzer* nie etwas
   * tut; hier ist es ein Zustand, der sich in Sekunden von selbst auflöst.
   * Verschwände „Erneut senden" für eine Minute, suchte jemand nach dem einen
   * Weg, den die Seite an dieser Stelle anbietet, und fände nichts. Er bleibt
   * deshalb stehen, sichtbar untätig, und der Satz daneben sagt, bis wann.
   */
  readonly disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        font: "inherit",
        fontWeight: 700,
        /* Gedämpft, damit man ihn nicht für bedienbar hält — die Auskunft
           steht im Text daneben, nicht in der Farbe allein. */
        color: disabled ? "var(--text-faint)" : "var(--text-brand)",
        background: "none",
        border: "none",
        padding: 0,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

/**
 * Die Fortschrittsanzeige über einem mehrstufigen Vorgang (HM-060).
 *
 * **Zwei Schritte und nicht drei.** Der Entwurf zeigt „Schritt 1 von 3" und als
 * dritten eine Seite „Konto ist bereit" mit einer Liste: E-Mail bestätigt,
 * erste Anlage noch offen, Zwei-Faktor-Schutz empfohlen. Von den drei Zeilen
 * stimmt hier keine: Die erste beschreibt den Schritt, den man gerade getan
 * hat, die zweite ist falsch (die Anlage entsteht mit dem Konto, HM-027), und
 * die dritte verspricht etwas, das dieses Produkt nicht hat. Übrig bliebe eine
 * Seite, die den Weg ins Dashboard um einen Klick verlängert.
 */
export function AuthSteps({
  current,
  total,
}: {
  readonly current: number;
  readonly total: number;
}) {
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8 }}
      /* Die Balken sind Darstellung; gelesen wird der Satz rechts daneben. */
      aria-hidden="true"
    >
      {Array.from({ length: total }, (_unused, index) => (
        <span
          key={index}
          style={{
            height: 4,
            flex: 1,
            borderRadius: "var(--radius-pill)",
            background: index < current ? "var(--hm-green-500)" : "var(--border-default)",
          }}
        />
      ))}
      <span style={{ flex: "none", fontSize: 12, fontWeight: 600, color: "var(--text-muted)" }}>
        Schritt {current} von {total}
      </span>
    </div>
  );
}

/**
 * Der Weg zurück über dem Formular — „‹ Angaben ändern".
 *
 * **Ein Knopf und kein Link**, wie `AuthSwitch`: Die Stufen der Registrierung
 * haben keine eigene Adresse, weil der Zwischenstand nur im Browser lebt. Ein
 * `href` müsste eine Adresse benennen, die nach einem Neuladen nichts mehr
 * zeigt.
 */
export function AuthBack({
  onClick,
  children,
}: {
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        alignSelf: "flex-start",
        font: "inherit",
        fontSize: 13.5,
        fontWeight: 600,
        color: "var(--text-body)",
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }}
    >
      ‹ {children}
    </button>
  );
}

/**
 * Der Weg zurück auf eine Ansicht, die **eine Adresse hat** — „‹ Zurück zur
 * Anmeldung".
 *
 * Das Gegenstück zu `AuthBack`, und der Unterschied ist keine Kosmetik: Die
 * Stufen der Registrierung leben im Browser und haben keine Adresse, deshalb
 * ist der Weg dorthin ein Knopf. Die Anmeldung hat eine — `/` —, und ein Knopf
 * an ihrer Stelle nähme dem Strg-Klick den neuen Tab und der Sprachausgabe die
 * Auskunft, dass hier ein Ziel steht (dieselbe Überlegung wie bei der
 * Gerätezeile, HM-038).
 */
export function AuthBackLink({
  to,
  children,
}: {
  readonly to: Route;
  readonly children: ReactNode;
}) {
  return (
    <AuthLink to={to} style={{ alignSelf: "flex-start", color: "var(--text-body)" }}>
      ‹ {children}
    </AuthLink>
  );
}

/** Der abgesetzte Hinweiskasten am Fuß des Formulars. */
export function AuthNote({ children }: { readonly children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--sp-3)",
        padding: "var(--sp-4)",
        background: "var(--surface-sunken)",
        borderRadius: "var(--radius-md)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          marginTop: 5,
          flex: "none",
          borderRadius: "var(--radius-pill)",
          background: "var(--hm-green-500)",
        }}
      />
      <span style={{ fontSize: 13, lineHeight: 1.5, color: "var(--text-muted)" }}>{children}</span>
    </div>
  );
}
